<script setup>
// SiteUp: fluxo de agendamento de compromisso WAHA em etapas.
// mode="confirm" (padrão): só a confirmação — usado quando o horário já foi
// escolhido antes (ScheduleModal da Agenda dispara via evento global).
// mode="full": Horário → Confirmação — usado pelo popover Agenda da conversa,
// que antes fixava "amanhã 9h" sem o operador escolher nada.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocale } from 'shared/composables/useLocale';
import kanbanAPI from 'dashboard/api/kanban';
import FunnelAPI from 'dashboard/api/funnel';
import { emitter } from 'shared/helpers/mitt';
import DateTimeField from 'dashboard/components-next/datetime/DateTimeField.vue';
import DatePicker from 'dashboard/components-next/datetime/DatePicker.vue';
import TimeSlotPicker from 'dashboard/components-next/datetime/TimeSlotPicker.vue';
import {
  addDays,
  endOfDay,
  findConflict,
  findFreeSlots,
  formatMinutes,
  isoDate,
  minutesOfDate,
  nextBusinessOffset,
  normalizeAgendaItem,
  parseTimeToMinutes,
  startOfDay,
} from '../agenda/agendaHelpers';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  itemId: { type: [String, Number], default: null },
  saveAppointment: { type: Function, default: null },
  // ISO (UTC) do compromisso; pré-preenche data/hora nas duas etapas.
  scheduledAt: { type: String, default: '' },
  mode: {
    type: String,
    default: 'confirm',
    validator: value => ['confirm', 'full'].includes(value),
  },
  // Funil do card — habilita conflito/horários livres na etapa de horário.
  funnelId: { type: [String, Number], default: null },
});
const emit = defineEmits(['update:modelValue', 'sent']);

const { t } = useI18n();
const { resolvedLocale } = useLocale();

const TIME_CHIPS = [
  { key: '9h', min: 540 },
  { key: '10h30', min: 630 },
  { key: '14h', min: 840 },
  { key: '16h30', min: 990 },
  { key: 'other', min: null },
];
const DURATION_CHIPS = [15, 30, 45, 60];

const step = ref('confirm');
const loading = ref(false);
const sending = ref(false);
const saving = ref(false);
const saveFailed = ref(false);
const enabled = ref(true);
const message = ref('');
const previewFailed = ref(false);
const previewError = ref('');
const dayItems = ref([]);
const form = ref({
  scheduled_at: '',
  duration_minutes: 60,
  location: '',
  responsible_name: '',
  day: 'today',
  customDate: '',
  time: null,
  customTime: '',
});

const today = computed(() => startOfDay(new Date()));

// ISO/UTC -> "YYYY-MM-DDTHH:mm" no fuso do navegador (Brasília). O backend
// interpreta o valor naive em America/Sao_Paulo.
const toLocalInput = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const close = () => emit('update:modelValue', false);

// --- Etapa 1: horário -------------------------------------------------------

const weekdayShort = date =>
  date
    .toLocaleDateString(resolvedLocale.value, { weekday: 'short' })
    .replace('.', '');

const chip3 = computed(() => {
  const offset = nextBusinessOffset(2, today.value);
  const date = addDays(today.value, offset);
  return { label: `${weekdayShort(date)} ${date.getDate()}`, offset };
});

const dayChips = computed(() => [
  { key: 'today', offset: 0, label: t('KANBAN.AGENDA_BOARD.TODAY') },
  { key: 'tomorrow', offset: 1, label: t('KANBAN.AGENDA_BOARD.TOMORROW') },
  { key: 'chip3', offset: chip3.value.offset, label: chip3.value.label },
  { key: 'other', offset: null, label: t('KANBAN.AGENDA_BOARD.OTHER_DATE') },
]);

const resolvedOffset = computed(() => {
  const chip = dayChips.value.find(entry => entry.key === form.value.day);
  if (chip && chip.offset !== null) return chip.offset;
  if (!form.value.customDate) return null;
  const [year, month, day] = form.value.customDate.split('-').map(Number);
  const target = startOfDay(new Date(year, month - 1, day));
  return Math.round((target.getTime() - today.value.getTime()) / 86400000);
});

const resolvedDayDate = computed(() =>
  resolvedOffset.value == null
    ? null
    : addDays(today.value, resolvedOffset.value)
);

const resolvedStart = computed(() => {
  const chip = TIME_CHIPS.find(entry => entry.key === form.value.time);
  if (chip && chip.min !== null) return chip.min;
  return parseTimeToMinutes(form.value.customTime);
});

const timeReady = computed(
  () => resolvedDayDate.value !== null && resolvedStart.value !== null
);

const resolvedDayKey = computed(() =>
  resolvedDayDate.value ? isoDate(resolvedDayDate.value) : ''
);

const notBeforeMin = computed(() =>
  resolvedOffset.value === 0 ? minutesOfDate(new Date()) + 30 : null
);

const freeSlots = computed(() =>
  findFreeSlots(dayItems.value, {
    dayKey: resolvedDayKey.value,
    durationMin: form.value.duration_minutes,
    notBeforeMin: notBeforeMin.value,
  })
);

const scheduleConflict = computed(() => {
  if (!timeReady.value) return null;
  return findConflict(
    {
      id: props.itemId || -1,
      kind: 'kanban_item',
      dayKey: resolvedDayKey.value,
      startMin: resolvedStart.value,
      durationMin: form.value.duration_minutes,
      status: 'scheduled',
    },
    dayItems.value
  );
});

const fetchDayAgenda = async () => {
  if (!props.funnelId || !resolvedDayDate.value) {
    dayItems.value = [];
    return;
  }
  try {
    const { data } = await FunnelAPI.getAgenda(props.funnelId, {
      from: resolvedDayDate.value.toISOString(),
      to: endOfDay(resolvedDayDate.value).toISOString(),
    });
    dayItems.value = (Array.isArray(data) ? data : []).map(normalizeAgendaItem);
  } catch {
    dayItems.value = [];
  }
};

watch(resolvedDayKey, () => {
  if (props.mode === 'full') fetchDayAgenda();
});

const pickFreeSlot = min => {
  const chip = TIME_CHIPS.find(entry => entry.min === min);
  if (chip) {
    form.value.time = chip.key;
    return;
  }
  form.value.time = 'other';
  form.value.customTime = formatMinutes(min);
};

const dayChipLabel = chip => {
  if (chip.key !== 'other' || !form.value.customDate) return chip.label;
  const [year, month, day] = form.value.customDate.split('-');
  return `${day}/${month}/${String(year).slice(2)}`;
};

const timeChipLabel = chip => {
  if (chip.key !== 'other') return chip.key;
  return form.value.customTime || t('KANBAN.AGENDA_BOARD.OTHER_TIME');
};

const durationLabel = duration => `${duration}m`;

const stepLabels = computed(() => ({
  time: `1 · ${t('KANBAN.APPOINTMENT_MODAL.STEP_TIME')}`,
  confirm: `2 · ${t('KANBAN.APPOINTMENT_MODAL.STEP_CONFIRM')}`,
}));

// --- Etapa 2: confirmação ---------------------------------------------------

const summaryLabel = computed(() => {
  if (!form.value.scheduled_at) return '';
  const date = new Date(form.value.scheduled_at);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.toLocaleDateString(resolvedLocale.value, {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });
  const start = minutesOfDate(date);
  const end = start + Number(form.value.duration_minutes || 60);
  return `${day} · ${formatMinutes(start)} – ${formatMinutes(end)}`;
});

const buildPayload = () => ({
  scheduled_at: form.value.scheduled_at,
  duration_minutes: form.value.duration_minutes,
  location: form.value.location,
  responsible_name: form.value.responsible_name,
});

const refreshPreview = async () => {
  if (!props.itemId) return;
  loading.value = true;
  previewFailed.value = false;
  previewError.value = '';
  try {
    const { data } = await kanbanAPI.previewAppointment(
      props.itemId,
      buildPayload()
    );
    enabled.value = data.enabled !== false;
    message.value = data.message || '';
    // Autopreenche local/responsável a partir da config do Kanban se vazios.
    if (!form.value.location && data.event?.location) {
      form.value.location = data.event.location;
    }
    if (!form.value.responsible_name && data.event?.responsible_name) {
      form.value.responsible_name = data.event.responsible_name;
    }
    if (props.mode === 'confirm' && data.event?.duration_minutes) {
      form.value.duration_minutes = data.event.duration_minutes;
    }
  } catch (e) {
    previewFailed.value = true;
    previewError.value = e?.response?.data?.error || e?.message || '';
    message.value = '';
  } finally {
    loading.value = false;
  }
};

const goToConfirm = () => {
  if (!timeReady.value) return;
  const p = n => String(n).padStart(2, '0');
  const day = resolvedDayDate.value;
  form.value.scheduled_at = `${isoDate(day)}T${p(Math.floor(resolvedStart.value / 60))}:${p(resolvedStart.value % 60)}`;
  step.value = 'confirm';
  refreshPreview();
};

const backToTime = () => {
  step.value = 'time';
  previewFailed.value = false;
  previewError.value = '';
};

const send = async () => {
  if (!props.itemId || sending.value) return;
  sending.value = true;
  try {
    await kanbanAPI.sendAppointmentConfirmation(props.itemId, buildPayload());
    emitter.emit('newToastMessage', {
      message: t('KANBAN.APPOINTMENT_MODAL.SENT'),
      type: 'success',
    });
    emit('sent');
    close();
  } catch (e) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.APPOINTMENT_MODAL.SEND_ERROR', {
        error: e?.response?.data?.error || e?.message || '',
      }),
      type: 'error',
    });
  } finally {
    sending.value = false;
  }
};

// Salva a data no card sem mandar mensagem pro lead (o pai persiste — ele tem
// o item completo; persistir aqui com item_details parcial apagaria o resto).
const saveOnly = async () => {
  if (saving.value || !form.value.scheduled_at) return;
  saving.value = true;
  saveFailed.value = false;
  const requestedItemId = props.itemId;
  try {
    if (!props.saveAppointment) return;
    const saved = await props.saveAppointment({
      scheduledAt: form.value.scheduled_at,
    });
    if (props.itemId !== requestedItemId || !props.modelValue) return;
    if (saved === true) close();
    else saveFailed.value = true;
  } catch {
    saveFailed.value = true;
  } finally {
    saving.value = false;
  }
};

const canSend = computed(
  () =>
    enabled.value &&
    !sending.value &&
    !loading.value &&
    !previewFailed.value &&
    !!form.value.scheduled_at
);

// Ao abrir: reseta o estado conforme o modo.
watch(
  () => props.modelValue,
  open => {
    if (!open) return;
    message.value = '';
    previewFailed.value = false;
    previewError.value = '';
    enabled.value = true;
    dayItems.value = [];
    const prefill = props.scheduledAt ? new Date(props.scheduledAt) : null;
    const hasPrefill = prefill && !Number.isNaN(prefill.getTime());
    form.value = {
      scheduled_at: hasPrefill ? toLocalInput(props.scheduledAt) : '',
      duration_minutes: 60,
      location: '',
      responsible_name: '',
      day: 'today',
      customDate: '',
      time: null,
      customTime: '',
    };
    if (props.mode === 'confirm') {
      step.value = 'confirm';
      refreshPreview();
      return;
    }
    step.value = 'time';
    if (hasPrefill) {
      const offset = Math.round(
        (startOfDay(prefill).getTime() - today.value.getTime()) / 86400000
      );
      const chip = dayChips.value.find(entry => entry.offset === offset);
      form.value.day = chip ? chip.key : 'other';
      form.value.customDate = chip ? '' : isoDate(prefill);
      const startMin = minutesOfDate(prefill);
      const timeChip = TIME_CHIPS.find(entry => entry.min === startMin);
      form.value.time = timeChip ? timeChip.key : 'other';
      form.value.customTime = timeChip ? '' : formatMinutes(startMin);
    }
    fetchDayAgenda();
  }
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[2147483000] flex items-center justify-center bg-n-alpha-black2 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      @click.self="close"
    >
      <div
        class="flex max-h-[92vh] w-[520px] max-w-[94vw] flex-col overflow-y-auto rounded-3xl border border-n-weak bg-n-solid-1 p-6 shadow-2xl"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <h2 class="m-0 text-[17px] font-medium text-n-slate-12">
              {{ t('KANBAN.APPOINTMENT_MODAL.TITLE') }}
            </h2>
            <div
              v-if="mode === 'full'"
              class="mt-1.5 flex items-center gap-1.5 text-[11.5px] font-bold"
              data-testid="appointment-steps"
            >
              <span
                class="inline-flex h-5 items-center gap-1 rounded-full px-2"
                :class="
                  step === 'time'
                    ? 'bg-n-blue-9 text-white'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{ stepLabels.time }}
              </span>
              <span
                class="i-ri-arrow-right-s-line size-4 text-n-slate-10"
                aria-hidden="true"
              />
              <span
                class="inline-flex h-5 items-center gap-1 rounded-full px-2"
                :class="
                  step === 'confirm'
                    ? 'bg-n-blue-9 text-white'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{ stepLabels.confirm }}
              </span>
            </div>
          </div>
          <button
            type="button"
            class="flex size-8 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
            :aria-label="t('KANBAN.AGENDA_BOARD.CLOSE')"
            @click="close"
          >
            <span class="i-ri-close-line size-5" aria-hidden="true" />
          </button>
        </div>

        <!-- Etapa 1: horário -->
        <div v-if="step === 'time'" class="mt-4 flex flex-col gap-3.5">
          <div>
            <span
              class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
            >
              {{ t('KANBAN.AGENDA_BOARD.WHEN') }}
            </span>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="chip in dayChips"
                :key="chip.key"
                type="button"
                class="h-8 rounded-full px-3 text-[12.5px] font-bold"
                :class="
                  form.day === chip.key
                    ? 'bg-n-slate-12 text-n-solid-1'
                    : 'border border-n-weak text-n-slate-12 hover:bg-n-solid-2'
                "
                @click="form.day = chip.key"
              >
                {{ dayChipLabel(chip) }}
              </button>
            </div>
            <div v-if="form.day === 'other'" class="mt-2">
              <DatePicker v-model="form.customDate" />
            </div>
            <div class="mt-2 flex flex-wrap gap-1.5">
              <button
                v-for="chip in TIME_CHIPS"
                :key="chip.key"
                type="button"
                class="h-8 rounded-full px-3 text-[12.5px] font-bold tabular-nums"
                :class="
                  form.time === chip.key
                    ? 'bg-n-blue-9 text-white'
                    : 'border border-n-weak text-n-slate-12 hover:bg-n-solid-2'
                "
                @click="form.time = chip.key"
              >
                {{ timeChipLabel(chip) }}
              </button>
            </div>
            <div v-if="form.time === 'other'" class="mt-2">
              <TimeSlotPicker v-model="form.customTime" />
            </div>

            <div
              v-if="freeSlots.length"
              class="mt-2.5 flex flex-wrap items-center gap-1.5"
              data-testid="appointment-free-slots"
            >
              <span class="text-[11.5px] font-bold text-n-teal-11">
                {{ t('KANBAN.APPOINTMENT_MODAL.FREE_SLOTS') }}
              </span>
              <button
                v-for="slot in freeSlots"
                :key="slot"
                type="button"
                class="h-7 rounded-full border border-n-teal-9/40 bg-n-teal-9/10 px-2.5 text-[12px] font-bold tabular-nums text-n-teal-11 hover:bg-n-teal-9/20"
                @click="pickFreeSlot(slot)"
              >
                {{ formatMinutes(slot) }}
              </button>
            </div>

            <div
              v-if="scheduleConflict"
              class="mt-2 flex items-start gap-2 rounded-xl border border-n-amber-6 bg-n-amber-9/10 px-3 py-2 text-[12px] text-n-amber-11"
              data-testid="appointment-conflict"
            >
              <span
                class="i-ri-alert-line mt-0.5 size-4 shrink-0"
                aria-hidden="true"
              />
              <span>
                {{
                  t('KANBAN.AGENDA_BOARD.SCHEDULE_CONFLICT', {
                    name:
                      scheduleConflict.displayName || scheduleConflict.title,
                    range: `${formatMinutes(scheduleConflict.startMin)} – ${formatMinutes(
                      scheduleConflict.startMin + scheduleConflict.durationMin
                    )}`,
                  })
                }}
              </span>
            </div>
          </div>

          <div>
            <span
              class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
            >
              {{ t('KANBAN.AGENDA_BOARD.DURATION') }}
            </span>
            <div class="flex gap-1.5">
              <button
                v-for="duration in DURATION_CHIPS"
                :key="duration"
                type="button"
                class="h-8 flex-1 rounded-full text-[12.5px] font-bold tabular-nums"
                :class="
                  form.duration_minutes === duration
                    ? 'bg-n-slate-12 text-n-solid-1'
                    : 'border border-n-weak text-n-slate-12 hover:bg-n-solid-2'
                "
                @click="form.duration_minutes = duration"
              >
                {{ durationLabel(duration) }}
              </button>
            </div>
          </div>

          <div class="mt-1.5 flex justify-end gap-2">
            <button
              type="button"
              class="h-9 rounded-full px-4 text-[13px] font-bold text-n-slate-12 hover:bg-n-alpha-2"
              @click="close"
            >
              {{ t('KANBAN.AGENDA_BOARD.CLOSE') }}
            </button>
            <button
              type="button"
              class="h-9 rounded-full bg-n-blue-9 px-5 text-[13px] font-bold text-white hover:bg-n-blue-10 disabled:cursor-not-allowed disabled:opacity-40"
              :disabled="!timeReady"
              data-testid="appointment-continue"
              @click="goToConfirm"
            >
              {{ t('KANBAN.APPOINTMENT_MODAL.CONTINUE') }}
            </button>
          </div>
        </div>

        <!-- Etapa 2: confirmação -->
        <div v-else class="mt-4 flex flex-col gap-3.5">
          <div
            v-if="!enabled"
            class="rounded-xl border border-n-amber-9/35 bg-n-amber-9/10 px-3 py-2.5 text-[12px] text-n-amber-11"
          >
            {{ t('KANBAN.APPOINTMENT_MODAL.DISABLED_WARN') }}
          </div>

          <div
            v-if="mode === 'full'"
            class="flex items-center justify-between rounded-xl border border-n-weak bg-n-solid-2 px-3.5 py-2.5"
            data-testid="appointment-summary"
          >
            <span
              class="flex items-center gap-2 text-[13px] font-bold tabular-nums text-n-slate-12"
            >
              <span
                class="i-ri-calendar-event-line size-4 text-n-blue-11"
                aria-hidden="true"
              />
              {{ summaryLabel }}
            </span>
            <button
              type="button"
              class="text-[12.5px] font-bold text-n-blue-11 hover:underline"
              @click="backToTime"
            >
              {{ t('KANBAN.APPOINTMENT_MODAL.CHANGE_TIME') }}
            </button>
          </div>

          <div v-else class="grid grid-cols-2 gap-3">
            <div>
              <span
                class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.APPOINTMENT_MODAL.DATETIME') }}
              </span>
              <DateTimeField v-model="form.scheduled_at" />
            </div>
            <div>
              <span
                class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.AGENDA_BOARD.DURATION') }}
              </span>
              <input
                v-model.number="form.duration_minutes"
                type="number"
                min="5"
                class="h-10 w-full rounded-xl border border-n-weak bg-n-solid-2 px-2.5 text-[13px] font-medium text-n-slate-12"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <span
                class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.AGENDA_BOARD.LOCATION') }}
              </span>
              <input
                v-model="form.location"
                type="text"
                :placeholder="t('KANBAN.AGENDA_BOARD.LOCATION_PLACEHOLDER')"
                class="h-10 w-full rounded-xl border border-n-weak bg-n-solid-2 px-2.5 text-[13px] font-medium text-n-slate-12 placeholder:text-n-slate-10"
              />
            </div>
            <div>
              <span
                class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.AGENDA_BOARD.RESPONSIBLE') }}
              </span>
              <input
                v-model="form.responsible_name"
                type="text"
                class="h-10 w-full rounded-xl border border-n-weak bg-n-solid-2 px-2.5 text-[13px] font-medium text-n-slate-12"
              />
            </div>
          </div>

          <div>
            <span
              class="mb-1.5 flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
            >
              <span
                class="i-ri-whatsapp-line size-3.5 text-n-teal-11"
                aria-hidden="true"
              />
              {{ t('KANBAN.APPOINTMENT_MODAL.PREVIEW_LABEL') }}
            </span>

            <div
              v-if="loading"
              class="space-y-1.5 rounded-2xl rounded-bl-md border border-n-weak bg-n-solid-2 p-3"
              data-testid="appointment-preview-loading"
            >
              <div class="h-3 w-3/4 animate-pulse rounded bg-n-alpha-2" />
              <div class="h-3 w-full animate-pulse rounded bg-n-alpha-2" />
              <div class="h-3 w-1/2 animate-pulse rounded bg-n-alpha-2" />
            </div>

            <div
              v-else-if="previewFailed"
              class="rounded-xl border border-n-ruby-9/30 bg-n-ruby-9/10 px-3.5 py-3"
              data-testid="appointment-preview-error"
            >
              <p
                class="m-0 flex items-center gap-2 text-[13px] font-bold text-n-ruby-11"
              >
                <span
                  class="i-ri-error-warning-line size-4 shrink-0"
                  aria-hidden="true"
                />
                {{ t('KANBAN.APPOINTMENT_MODAL.PREVIEW_ERROR') }}
              </p>
              <p
                v-if="previewError"
                class="m-0 mt-1 text-[11.5px] text-n-slate-10"
              >
                {{ previewError }}
              </p>
              <button
                type="button"
                class="mt-2 h-8 rounded-full border border-n-ruby-9/40 px-3 text-[12.5px] font-bold text-n-ruby-11 hover:bg-n-ruby-9/10"
                @click="refreshPreview"
              >
                {{ t('KANBAN.APPOINTMENT_MODAL.RETRY') }}
              </button>
            </div>

            <div
              v-else
              class="whitespace-pre-wrap rounded-2xl rounded-bl-md border border-n-teal-9/30 bg-n-teal-9/10 p-3 text-[13px] leading-relaxed text-n-slate-12"
              data-testid="appointment-preview"
            >
              {{ message || t('KANBAN.APPOINTMENT_MODAL.PREVIEW_EMPTY') }}
            </div>
          </div>

          <div class="mt-1.5 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              class="h-9 rounded-full border border-n-weak px-4 text-[13px] font-bold text-n-blue-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-40"
              :disabled="loading"
              @click="refreshPreview"
            >
              {{ t('KANBAN.APPOINTMENT_MODAL.REFRESH_PREVIEW') }}
            </button>
            <p
              v-if="saveFailed"
              role="alert"
              class="m-0 text-sm text-n-ruby-11"
            >
              {{ t('KANBAN.APPOINTMENT_MODAL.SAVE_ERROR') }}
            </p>
            <button
              v-if="mode === 'full'"
              type="button"
              class="h-9 rounded-full border border-n-weak px-4 text-[13px] font-bold text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-40"
              :disabled="saving || !form.scheduled_at"
              data-testid="appointment-save-only"
              @click="saveOnly"
            >
              {{ t('KANBAN.APPOINTMENT_MODAL.SAVE_ONLY') }}
            </button>
            <button
              type="button"
              class="h-9 rounded-full bg-n-blue-9 px-5 text-[13px] font-bold text-white hover:bg-n-blue-10 disabled:cursor-not-allowed disabled:opacity-40"
              :disabled="!canSend"
              data-testid="appointment-send"
              @click="send"
            >
              {{
                sending
                  ? t('KANBAN.APPOINTMENT_MODAL.SENDING')
                  : t('KANBAN.APPOINTMENT_MODAL.SEND')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
